import React, { FC } from 'react';

import { localizePhoneNumbers, isPhoneNumber } from '@lucid/utils';
import { mqMin } from '@lucid/theme';

import { LucidTheme } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Text from '../text/text';
import Link from '../link/link';

export interface PreFooterCtaEntity {
  fieldButtonLink?: string;
  fieldButtonText?: string;
  fieldIsSecondary?: boolean;
}

export interface PreFooterCtaProps {
  hasExtraPadding?: boolean;
  header?: string | null;
  subheader?: string | null;
  pitch?: string | null;
  ctas?: PreFooterCtaEntity[] | null;
  orCallCta?: {
    fieldCallCtaPhoneNumber?: string;
    fieldCallCtaText?: string;
  } | null;
  variant?: 'dark' | 'light' | 'enterprise' | 'primary' | 'secondary' | 'selfServe' | 'integratedCampaign';
}

const PreFooterCta: FC<PreFooterCtaProps> = ({
  hasExtraPadding,
  header,
  subheader,
  pitch,
  ctas,
  orCallCta,
  variant = 'dark',
}) => {
  const whiteTextVariants = ['dark', 'primary', 'secondary'];
  const willUseWhiteText = whiteTextVariants.includes(variant);

  return (
    <Box
      css={(theme: LucidTheme) => [
        {
          backgroundColor: 'white',
        },
        variant === 'dark' && {
          backgroundColor: theme.colors.coolGrey[80],
        },
        variant === 'enterprise' && {
          backgroundColor: theme.colors.coolGrey[5],
        },
        variant === 'primary' && {
          backgroundColor: theme.colors.primary,
        },
        variant === 'secondary' && {
          backgroundColor: theme.colors.secondary,
        },
        variant === 'selfServe' && {
          backgroundColor: theme.colors.blue[10],
        },
        variant === 'integratedCampaign' && {
          backgroundColor: theme.colors.blue[40],
        },
      ]}
      pb={hasExtraPadding ? 9 : 8}
      pt={hasExtraPadding ? 9 : 5}
    >
      <Box
        css={{
          maxWidth: '64em',
          margin: '0 auto',
          textAlign: 'center',
        }}
        px={4}
      >
        {header && (
          <Heading
            as="h2"
            css={(theme: LucidTheme) => willUseWhiteText && { color: theme.colors.white }}
            variant={isPhoneNumber.test(header) ? 'headline2XL' : 'headlineXL'}
          >
            {isPhoneNumber.test(header) ? (
              <Link
                css={(theme: LucidTheme) => [
                  {
                    fontWeight: theme.fontWeights.stronger,
                    '&:hover': {
                      color: willUseWhiteText ? theme.colors.white : theme.colors.coolGrey[100],
                    },
                  },
                  willUseWhiteText && { color: theme.colors.white },
                ]}
                to={`tel:+1${header}`}
              >
                {localizePhoneNumbers(header)}
              </Link>
            ) : (
              localizePhoneNumbers(header)
            )}
          </Heading>
        )}
        {subheader && (
          <Text
            css={(theme: LucidTheme) => [
              willUseWhiteText && {
                color: theme.colors.white,
              },
            ]}
            variant="paragraphM400"
          >
            {subheader}
          </Text>
        )}
        {pitch && (
          <Text
            css={(theme: LucidTheme) => [
              willUseWhiteText && {
                color: theme.colors.white,
              },
            ]}
            variant="paragraphS400"
          >
            {pitch}
          </Text>
        )}
        {ctas &&
          ctas.map(({ fieldButtonLink, fieldButtonText }) => {
            if (fieldButtonLink) {
              return (
                <Link
                  key={fieldButtonText}
                  displayAsButton
                  css={{
                    width: '100%',
                    [mqMin.mobile]: {
                      width: 'auto',
                    },
                  }}
                  to={fieldButtonLink}
                  variant={willUseWhiteText ? 'secondary' : 'primary'}
                >
                  {fieldButtonText}
                </Link>
              );
            }
            return;
          })}
        {orCallCta && (
          <Link to={`tel:+1${orCallCta.fieldCallCtaPhoneNumber}`}>
            {orCallCta.fieldCallCtaText}
            {orCallCta.fieldCallCtaPhoneNumber}
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default PreFooterCta;
